import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiUpdateRemoteColor } from "../../api/backend/user";
import { apiLogoutMember } from "../../api/backend/auth";
import {
  getCookie,
  clearAllCookies,
  setCookie,
  MEMBER_ID_COOKIE_NAME,
} from "../../utils/cookies";
import { FORM_FLOW_CONTROL } from "../../constants/currentContextTypes";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [
    countRefreshLoggedinAcctHldrProfileLoad,
    setCountRefreshLoggedinAcctHldrProfileLoad,
  ] = useState(0);
  const [successFlashMessage, setSuccessFlashMessage] = useState(null);
  const [openSkitUploadForm, setOpenSkitUploadForm] = useState(false);
  const [notInterested, setNotInterested] = useState([]);
  const [currentFormStep, setCurrentFormStep] = useState(
    FORM_FLOW_CONTROL.SIGN_IN_FORM
  );
  const [currentContextType, setCurrentContextType] = useState(
    FORM_FLOW_CONTROL.SIGN_IN_FORM
  );
  const [currentMemberId, setCurrentMemberId] = useState(
    () => getCookie(MEMBER_ID_COOKIE_NAME) || null
  );
  const [currentMemberData, setCurrentMemberData] = useState(null);
  const [playCount, setPlayCount] = useState(
    () => parseInt(localStorage.getItem("playCount"), 10) || 0
  );
  const [verifyNeeded, setVerifyNeeded] = useState(false);
  const [remoteColor, setRemoteColor] = useState(
    () => localStorage.getItem("remoteColor") ?? ""
  );
  useEffect(() => {
    remoteColor
      ? localStorage.setItem("remoteColor", remoteColor)
      : localStorage.removeItem("remoteColor");
  }, [remoteColor]);

  useEffect(() => {
    console.log("[AuthProvider] MOUNT");
    return () => console.log("[AuthProvider] UNMOUNT");
  }, []);
  const refreshLoggedinAccountHolderProfile = useCallback(() => {
    setCountRefreshLoggedinAcctHldrProfileLoad((n) => n + 1);
  }, []);

  const redirectToLanding = useCallback(() => {
    // 1) Try client-side route change
    try {
      navigate("/", { replace: true });
    } catch (_) {}
    // 2) Belt-and-suspenders: hard replace shortly after
    setTimeout(() => {
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.replace("/");
      }
    }, 100);
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      console.group("[AuthCtx] handleLogout");
      console.log("called with currentMemberId:", currentMemberId);
      if (currentMemberId) {
        try {
          await apiLogoutMember(currentMemberId); // server logout
        } catch (_) {
          // swallow; still proceed with client reset
        }
      }
      // Clear client-side state
      clearAllCookies(); // Remove the cookie on the client-side if needed
      localStorage.clear();
      setCurrentMemberId(null);
      setPlayCount(0);
      setCurrentMemberData(null);
      redirectToLanding();
      console.log("flow reset → ctx=signInForm, step=signInForm");
    } catch (error) {
      console.error("Logout failed", error);
    }
  }, [currentMemberId, redirectToLanding]);

  // Logged wrappers
  const logSetCurrentFormStep = useCallback((next) => {
    const ts = new Date().toISOString();
    console.warn(`[AuthCtx][${ts}] setCurrentFormStep ->`, next);
    if (next === FORM_FLOW_CONTROL.BUILD_PROFILE_FORM) {
      console.trace(
        `[AuthCtx] TRACE: setCurrentFormStep(BUILD_PROFILE_FORM) caller stack`
      );
    }
    setCurrentFormStep(next);
  }, []);

  const logSetCurrentContextType = useCallback((next) => {
    const ts = new Date().toISOString();
    console.warn(`[AuthCtx][${ts}] setCurrentContextType ->`, next);
    setCurrentContextType(next);
  }, []);

  // to do assign the token for auto signin and if user tries to signin with a token
  // it could be because the user crashed so how to handle that since we have it stored in cookies
  // user will get an error..we could just relog the user in since it exist already and consider that crash state
  // in the backend

  // inside AuthProvider, near your other effects:
  useEffect(() => {
    console.log("[AuthCtx] currentContextType →", currentContextType);
  }, [currentContextType]);

  const remoteColorUserSetting = async (
    loggedInAccountHolderId,
    newRemoteColor
  ) => {
    try {
      const res = await apiUpdateRemoteColor(
        loggedInAccountHolderId,
        newRemoteColor
      );

      // eject if server says suspended
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout();
        return { count: null, error: true, suspended: true };
      }

      // normalize color from response
      const currentRemoteColor =
        res?.data?.remoteColor ??
        res?.data?.user?.remoteColor ??
        newRemoteColor;

      // state updates
      setCurrentMemberData((prev) =>
        prev ? { ...prev, remoteColor: currentRemoteColor } : prev
      );
      setRemoteColor(currentRemoteColor);

      // unread count (if backend included it)
      const count =
        typeof res?.data?.unreadNotificationCount === "number"
          ? res.data.unreadNotificationCount
          : null;

      return { count, currentRemoteColor };
    } catch (error) {
      // eject on suspension in error responses
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout();
        return { count: null, error: true, suspended: true };
      }

      // try to bubble a count even when the call failed
      const count =
        typeof error?.response?.data?.unreadNotificationCount === "number"
          ? error.response.data.unreadNotificationCount
          : typeof error?.unreadNotificationCount === "number"
            ? error.unreadNotificationCount
            : null;

      console.error("Failed to update remote color:", {
        msg: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      return { count, error: true };
    }
  };

  const loginMemberApproved = useCallback(async (memberId) => {
    console.log("loginMemberApproved called with memberId:", memberId);
    if (!memberId) {
      console.warn("loginMemberApproved aborted: no memberId");
      return;
    }
    // 1) persist their ID
    setCurrentMemberId(memberId);
    setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    console.log("[AuthCtx] setCurrentContextType(SIGN_IN_FORM) queued");
    setCookie(MEMBER_ID_COOKIE_NAME, memberId, /* days= */ 7);
    console.log(`Cookie set: ${MEMBER_ID_COOKIE_NAME}=${memberId}`);
    // 2) reset any counters
    localStorage.setItem("playCount", "0");
    setPlayCount(0);
    console.log("playCount reset");
    // (Remote color fetch removed)
  }, []);


  return (
    <AuthContext.Provider
      value={{
        openSkitUploadForm,
        setOpenSkitUploadForm,
        currentFormStep,
        setCurrentFormStep: logSetCurrentFormStep,
        currentContextType,
        setCurrentContextType: logSetCurrentContextType,
        verifyNeeded,
        setVerifyNeeded,
        // ID that survived signup → login
        currentMemberId,
        setCurrentMemberId,
        // Call once OTP/skip completes, to “really” log them in
        loginMemberApproved,
        // Call once you know they’re fully “complete” to grab the rest
        // fetchCurrentMemberAttributes,
        // The fully-fetched user object (firstName, lastName, email…)
        currentMemberData,
        setCurrentMemberData,
        // “Let's the member set the desire remote color
        remoteColor,
        setRemoteColor,
        remoteColorUserSetting,
        // Standard logout
        handleLogout,
        // Play-count & setter used to track when an add plays right now every third play an ad will play,
        // used with a links list
        playCount,
        setPlayCount,

        // needed so I can re-invoke functions when the user is logged and clicks the profile page, the call back is set here in the context so it can invoke the function in turn remounting the whole component to run the functions so it is a hard re-fresh
        countRefreshLoggedinAcctHldrProfileLoad, // used for count, when this changes it will invoke the other funnctions in the useEffect on the profile page, which will help me get the user's notifications and check suspension in the middleware
        refreshLoggedinAccountHolderProfile, // when the logged in account holder clicks this in the remote, it will change count so state changes which will invoke the other function in the profile
        // used as a container object to hold all not interested skits, this will be hidden from the member's view
        notInterested,
        setNotInterested,
        // set at the top of the app once to be passed down to any component that will flash a success message for any function that needs this setter and variable
        successFlashMessage,
        setSuccessFlashMessage,
        authReady, 
        setAuthReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
